import { FC, FormEvent, useState } from 'react';

import Button from '@ingka/button';
import FormField from '@ingka/form-field';
import InlineMessage from '@ingka/inline-message';
import InputField from '@ingka/input-field';
import QuestionMarkIcon from '@ingka/ssr-icon/paths/question-mark-circle';
import WarningTriangleIcon from '@ingka/ssr-icon/paths/warning-triangle';
import { t } from '@onlinepayments/payments-library';

import { postPayOrderLink } from '../../../services/payorderApi';
import { getErrorMessageIdByErrorCode, validateEmail, validateOrderNumber } from '../utils';

import '../../../styles/utility-classes.scss';
import './LinkForm.scss';

interface ILinkFormProps {
  onShowInfoClick(isOpen: boolean): void;
}

const LinkForm: FC<ILinkFormProps> = ({ onShowInfoClick }) => {
  const [shouldValidate, setShouldValidate] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [serverError, setServerError] = useState<string>('');
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setShouldValidate(true);
    setServerError('');

    if (!validateOrderNumber(orderNumber) || !validateEmail(email)) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await postPayOrderLink({
        customerEmail: email,
        orderNumber,
      });

      if ('errorCode' in result) {
        const errorMessageId = getErrorMessageIdByErrorCode(result.errorCode);
        setServerError(t(errorMessageId));
      } else {
        window.location.href = result.payOrderLink;
      }
    } catch (error) {
      const errorMessageId = getErrorMessageIdByErrorCode(error?.errorCode);
      setServerError(t(errorMessageId));
    }
    setIsLoading(false);
  };

  return (
    <form className="link-form" data-testid="linkForm" onSubmit={handleSubmit}>
      <h1 className="link-form__title">{t('link.link_form_title')}</h1>
      <p className="link-form__message">{t('link.link_form_message')}</p>
      <FormField
        valid={validateOrderNumber(orderNumber)}
        shouldValidate={shouldValidate && !validateOrderNumber(orderNumber)}
        validation={{
          msg: t('link.link_form_order_error'),
          id: 'order-number-invalid-error',
        }}
      >
        <InputField
          id="order-number"
          data-testid="orderNumber"
          type="text"
          ssrIcon={QuestionMarkIcon}
          iconPosition="trailing"
          iconOnClick={onShowInfoClick as () => void}
          iconClickDescription={t('link.link_form_order_how')}
          label={t('link.link_form_order_label')}
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
        />
      </FormField>
      <FormField
        valid={validateEmail(email)}
        shouldValidate={shouldValidate && !validateEmail(email)}
        validation={{
          msg: t('link.link_form_email_error'),
          id: 'email-invalid-error',
        }}
      >
        <InputField
          formNoValidate
          data-testid="email"
          id="email"
          type="email"
          label={t('link.link_form_email_label')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormField>
      {serverError && (
        <InlineMessage body={serverError} ssrIcon={WarningTriangleIcon} subtle variant="negative" />
      )}
      <Button
        className="mt-150"
        fluid
        data-testid="submit"
        type="emphasised"
        htmlType="submit"
        loading={isLoading}
        text={t('link.link_form_submit')}
      />
    </form>
  );
};

export default LinkForm;
